import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { APP_ENDPOINTS } from 'app/endpoints';
import { createEmptyData } from 'constants/emptyData';
import { ResponseNestedProductCategoryDto } from 'features/nested-product-category/types/dtos';
import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { formatDate } from 'utils/date-helpers';
import { formatPrice } from 'utils/price-helpers';
import { constructQueryParams } from 'utils/query-params-builder';
import { ResponseNestedProductDto } from '../types/dtos';
import { NESTED_PRODUCT_STATUS } from '../constants/status';

export type NestedProductsWithJoinQueryParams = BaseQueryParams & {
    placeId?: string;
};

export default (builder: EndpointBuilder<BaseQueryFn, string, string>) =>
    builder.query<PaginatedResponse<ResponseNestedProductDto>, NestedProductsWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const { placeId, ...cleanParams } = _arg;
            if (!placeId) {
                return {
                    data: createEmptyData<ResponseNestedProductDto>(),
                };
            }

            const nestedProductsResponse = await fetchWithBQ(
                constructQueryParams(`${APP_ENDPOINTS.NESTED_PRODUCTS}/place/${_arg.placeId}`, cleanParams),
            );
            const nestedProductsError = checkError(nestedProductsResponse);
            if (nestedProductsError) return nestedProductsError;
            const nestedProducts = nestedProductsResponse.data as PaginatedResponse<ResponseNestedProductDto>;

            const nestedProductCategoryIds = removeUndefined(
                handleDuplicates(nestedProducts.content.map((nestedProduct) => nestedProduct.nestedProductCategoryId)),
            );
            const nestedProductCategoriesResponse = await fetchResponse(
                APP_ENDPOINTS.NESTED_PRODUCT_CATEGORIES,
                nestedProductCategoryIds,
                fetchWithBQ,
            );
            const nestedProductCategoryError = checkError(nestedProductCategoriesResponse);
            if (nestedProductCategoryError) return nestedProductCategoryError;
            const nestedProductCategories =
                nestedProductCategoriesResponse.data as PaginatedResponse<ResponseNestedProductCategoryDto>;

            nestedProducts.content = nestedProducts.content.map((nestedProduct) => {
                const nestedProductCategoryName = nestedProductCategories.content.find((nestedProductCategory) => {
                    return nestedProductCategory.id === nestedProduct.nestedProductCategoryId;
                })?.name;

                const isAvailable = nestedProduct.isAvailable
                    ? `${NESTED_PRODUCT_STATUS.AVAILABLE}`
                    : `${NESTED_PRODUCT_STATUS.UNAVAILABLE}`;

                const createdAt = formatDate(nestedProduct.createdAt);

                const price = formatPrice(nestedProduct.price);

                const discountedPrice =
                    !isNaN(Number(nestedProduct.discountedPrice)) &&
                    nestedProduct.discountedPrice !== '0' &&
                    nestedProduct.discountedPrice !== null
                        ? formatPrice(nestedProduct.discountedPrice)
                        : '';

                return {
                    ...nestedProduct,
                    placeId: placeId,
                    createdAt,
                    isAvailable,
                    nestedProductCategoryName,
                    price,
                    discountedPrice,
                };
            });

            return {
                data: nestedProducts,
            };
        },
        providesTags: [{ type: 'nested-products-with-join', id: 'GET-JOIN' }],
    });
